import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast';
import {
  HistoryGridPayload,
  OrderHistoryFinancialModel,
  OrderHistoryModel,
} from '@store/customers/customerDetails/orderHistory';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import get from 'lodash/get';

export async function fetchOrderHistoryGridData(
  payload: HistoryGridPayload,
): Promise<OrderHistoryModel | undefined> {
  try {
    const response: V4ApiResponse<OrderHistoryModel> = await apiClient.post(
      'customer/customerorderhistory',
      payload,
    );
    if ('value' in response) {
      return get(response, 'value');
    }
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch Order History Data');
    handleToast(ToastType.Error, e.message);
  }
}

// Column order and header mapping aligned with AG Grid column definitions
const csvColumnConfig: { field: string; header: string }[] = [
  { field: 'customerId', header: 'Customer Id' },
  { field: 'orderId', header: 'Order Id' },
  { field: 'loadNumber', header: 'Load #' },
  { field: 'status', header: 'Status' },
  { field: 'originCity', header: 'Origin City' },
  { field: 'originState', header: 'Origin State' },
  { field: 'pickUp', header: 'Pick Date' },
  { field: 'destinationCity', header: 'Destination City' },
  { field: 'destinationState', header: 'Dest State' },
  { field: 'dropoff', header: 'Drop Date' },
  { field: 'referenceNumber', header: 'Ref #' },
  { field: 'proNumber', header: 'Pro #' },
  { field: 'trackingNumber', header: 'Tracking #' },
  { field: 'stopPickupNumber', header: 'Stop: Pickup #' },
  { field: 'stopDropOffNumber', header: 'Stop: Dropoff #' },
  { field: 'numLoads', header: 'Loads' },
  { field: 'loadProduct', header: 'Load Product' },
  { field: 'equipment', header: 'Equipment' },
  { field: 'carrierName', header: 'Carrier Name' },
  { field: 'agent', header: 'Agent' },
  { field: 'revenue', header: 'Revenue' },
  { field: 'cost', header: 'Cost' },
  { field: 'profit', header: 'Profit' },
];

function convertToCSV(data: Record<string, unknown>[]): string {
  if (data.length === 0) return '';
  const headerRow = csvColumnConfig.map((col) => col.header).join(',');
  const rows = data.map((row) =>
    csvColumnConfig
      .map((col) => {
        const value = row[col.field];
        const cell = value === null || value === undefined ? '' : String(value);
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      })
      .join(','),
  );
  return [headerRow, ...rows].join('\n');
}

export async function exportOrderHistoryToExcel(
  payload: HistoryGridPayload,
): Promise<void> {
  try {
    const response: V4ApiResponse<unknown> = await apiClient.post(
      'customer/customerorderhistoryexport',
      payload,
    );

    const responseData = 'value' in response ? response.value : response;

    let csvString: string;
    if (typeof responseData === 'string') {
      csvString = responseData;
    } else if (Array.isArray(responseData)) {
      csvString = convertToCSV(responseData as Record<string, unknown>[]);
    } else {
      csvString = '';
    }

    if (csvString) {
      const a = document.createElement('a');
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(
        new Blob([csvString], { type: 'text/csv;charset=utf-8;' }),
      );
      a.href = url;
      a.download = 'OrderHistory.csv';
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 0);
    }
  } catch (error: unknown) {
    const e = createError(error, 'Failed to export Order History Data');
    handleToast(ToastType.Error, e.message);
  }
}

export async function fetchOrderHistoryFinancial(
  payload: HistoryGridPayload,
): Promise<OrderHistoryFinancialModel | undefined> {
  try {
    const response: V4ApiResponse<OrderHistoryFinancialModel> =
      await apiClient.post('customer/customerorderhistoryfinancial', payload);
    if ('value' in response) {
      return get(response, 'value') as OrderHistoryFinancialModel;
    }
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to fetch Order History Financial Data',
    );
    handleToast(ToastType.Error, e.message);
  }
}
